import { QueryObserverOptions, useQuery } from '@tanstack/react-query'
import { useAuth } from '../auth/useAuth'
import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { TaskService } from '../../lib/api'
import { DefaultGetParamsProps } from '../../lib/api/types/interfaces'
import { GetTaskStatusResponse } from '../../lib/api/types/task.interfaces'
import { useDownloadProgress } from '../../context/progressContext'

export const useTaskStatus = (
	{ id, silentMode = true }: DefaultGetParamsProps & { silentMode?: boolean },
	options?: Partial<QueryObserverOptions<GetTaskStatusResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	const [progressToastId, setProgressToastId] =
		useState<React.ReactText | null>(null)

	const query = useQuery<GetTaskStatusResponse>({
		queryKey: id ? ['task-status', id] : [],
		queryFn: () => TaskService.getTaskStatus({ id }),
		enabled: isAuthenticated && Boolean(id),
		refetchInterval: ({ state }) => {
			return state?.data?.data?.data?.state === 0 ? 1000 : false
		},
		...options,
	})

	const taskData = query.data?.data?.data

	useEffect(() => {
		if (!taskData || silentMode) return

		const { state, result } = taskData

		if (state === 1 && result) {
			if (progressToastId && toast.isActive(progressToastId)) {
				toast.dismiss(progressToastId)
			}
			setProgressToastId(null)
			window.location.href = result
		}

		if (state === 2) {
			if (progressToastId && toast.isActive(progressToastId)) {
				toast.dismiss(progressToastId)
			}
			setProgressToastId(null)
		}
	}, [taskData?.progress, taskData?.state])

	return query
}

export const useDownloadTaskStatus = (
	{
		id,
		downloadItemId,
		downloadItemName,
		downloadItemType,
	}: DefaultGetParamsProps & {
		downloadItemId?: string
		downloadItemName?: string
		downloadItemType?: number
	},
	options?: Partial<QueryObserverOptions<GetTaskStatusResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	const { setDownloadStatuses, downloadStatuses } = useDownloadProgress()

	const downloadItem = downloadStatuses.find((item) => item.taskId === id)
	const isCanceled = downloadItem?.status === 'canceled'

	const downloadTriggeredRef = useRef<Record<string, boolean>>({})
	const canceledTasksRef = useRef<Set<string>>(new Set())

	useEffect(() => {
		if (isCanceled && id) {
			canceledTasksRef.current.add(id)
		}
	}, [isCanceled, id])

	const query = useQuery<GetTaskStatusResponse>({
		queryKey: id ? ['task-status', id] : [],
		queryFn: () => TaskService.getTaskStatus({ id }),
		enabled: isAuthenticated && Boolean(id && !isCanceled),
		refetchInterval: ({ state }) => {
			if (canceledTasksRef.current.has(id)) {
				return false
			}
			return state?.data?.data?.data?.state === 0 ? 1000 : false
		},
		...options,
	})

	const taskData = query.data?.data?.data

	useEffect(() => {
		if (!downloadItemId || !id) return

		setDownloadStatuses((prev) => {
			const existingIndex = prev.findIndex((item) => item.id === downloadItemId)
			if (existingIndex < 0) return prev

			const newStatuses = [...prev]
			newStatuses[existingIndex] = {
				...newStatuses[existingIndex],
				taskId: id,
			}
			return newStatuses
		})
	}, [downloadItemId, id, setDownloadStatuses])

	useEffect(() => {
		if (!taskData || !id) return

		if (canceledTasksRef.current.has(id)) {
			return
		}

		const { progress, state, result } = taskData

		setDownloadStatuses((prev) => {
			const existingIndex = prev.findIndex((item) => item.taskId === id)
			if (existingIndex < 0) return prev

			const existingItem = prev[existingIndex]

			if (existingItem.status === 'canceled') {
				return prev
			}

			const updatedItem = {
				...existingItem,
				status:
					state === 0
						? ('processing' as const)
						: state === 1
							? ('completed' as const)
							: ('failed' as const),
				progress: progress,
				error: state === 2 ? 'Download failed' : undefined,
			}

			const newStatuses = [...prev]
			newStatuses[existingIndex] = updatedItem
			return newStatuses
		})

		if (state === 1 && result && !downloadTriggeredRef.current[id]) {
			const existingItem = downloadStatuses.find((item) => item.taskId === id)
			if (existingItem?.status !== 'canceled') {
				downloadTriggeredRef.current[id] = true
				setTimeout(() => {
					window.location.href = result
				}, 500)
			}
		}
	}, [
		taskData?.progress,
		taskData?.state,
		taskData?.result,
		id,
		setDownloadStatuses,
	])

	return query
}
